/** @file puppetLayer: the puppet drawn into a pixel-art target, shown by a full-resolution overlay camera. */
import { Camera } from '@babylonjs/core/Cameras/camera.js';
import { TargetCamera } from '@babylonjs/core/Cameras/targetCamera.js';
import { Constants } from '@babylonjs/core/Engines/constants.js';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial.js';
import { RenderTargetTexture } from '@babylonjs/core/Materials/Textures/renderTargetTexture.js';
import { Texture } from '@babylonjs/core/Materials/Textures/texture.js';
import { Color4 } from '@babylonjs/core/Maths/math.color.js';
import { Vector3 } from '@babylonjs/core/Maths/math.vector.js';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder.js';
import type { Mesh } from '@babylonjs/core/Meshes/mesh.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import { DEPTH_RANGE } from '@zilo/pose3d-runtime-babylon/camera/depthRange';
import { overlayFrame } from './overlayFrame';
import { VIEWPORT_GROUPS } from './viewportGroups';
import { VIEWPORT_LAYERS } from './viewportLayers';
import type { ViewportLayout } from './viewportLayout';

/** The overlay camera, the quad and the current target (null until the first layout). */
export type PuppetLayer = { overlay: TargetCamera; quad: Mesh; target(): RenderTargetTexture | null; setLayout(layout: ViewportLayout, view: View): void };

/** Texture as is: the target holds premultiplied colour (sprites over a transparent clear). */
const VERTEX = 'precision highp float; attribute vec3 position; attribute vec2 uv; uniform mat4 worldViewProjection; varying vec2 vUV; void main() { vUV = uv; gl_Position = worldViewProjection * vec4(position, 1.0); }';
const FRAGMENT = 'precision highp float; varying vec2 vUV; uniform sampler2D puppet; void main() { gl_FragColor = texture2D(puppet, vUV); }';

/** The quad material: the target sampled as is, blended premultiplied over the grid. */
function quadMaterial(scene: Scene): ShaderMaterial {
  const m = new ShaderMaterial('viewport-puppet-material', scene, { vertexSource: VERTEX, fragmentSource: FRAGMENT }, {
    attributes: ['position', 'uv'], uniforms: ['worldViewProjection'], samplers: ['puppet'], needAlphaBlending: true,
  });
  Object.assign(m, { alphaMode: Constants.ALPHA_PREMULTIPLIED, backFaceCulling: false, disableDepthWrite: true });
  return m;
}

/** A nearest-sampled, mipmap-free `w x h` target cleared to transparent. */
function target(scene: Scene, w: number, h: number): RenderTargetTexture {
  const t = new RenderTargetTexture('viewport-puppet-target', { width: w, height: h }, scene, { generateMipMaps: false, samplingMode: Texture.NEAREST_SAMPLINGMODE, generateDepthBuffer: true });
  t.clearColor = new Color4(0, 0, 0, 0);
  t.wrapU = Texture.CLAMP_ADDRESSMODE;
  t.wrapV = Texture.CLAMP_ADDRESSMODE;
  return t;
}

/**
 * `puppet` (the existing ortho camera, layer `VIEWPORT_LAYERS.puppet`) renders the puppet and onion ghosts into a
 * `fit.width x fit.height` target; `overlay` (a child of it, so it shares position and turn; layer `overlay`) draws
 * the grid, a quad showing that target at `fit.scale` device pixels per target pixel, and the marks and gizmo at
 * full resolution. `scene.activeCameras = [puppet, overlay]`: one `scene.render()` draws both. `setLayout` (only on
 * a real size change) replaces the target and sets the overlay bounds from `overlayFrame`.
 */
export function puppetLayer(scene: Scene, puppet: TargetCamera): PuppetLayer {
  const overlay = new TargetCamera('viewport-overlay-camera', Vector3.Zero(), scene, false);
  Object.assign(overlay, { parent: puppet, mode: Camera.ORTHOGRAPHIC_CAMERA, minZ: DEPTH_RANGE.near, maxZ: DEPTH_RANGE.far, layerMask: VIEWPORT_LAYERS.overlay });
  puppet.layerMask = VIEWPORT_LAYERS.puppet;
  scene.activeCameras = [puppet, overlay];
  const quad = CreatePlane('viewport-puppet-quad', { size: 1 }, scene);
  const material = quadMaterial(scene);
  Object.assign(quad, { parent: overlay, material, layerMask: VIEWPORT_LAYERS.overlay, renderingGroupId: VIEWPORT_GROUPS.puppet, isPickable: false, alwaysSelectAsActiveMesh: true });
  let rtt: RenderTargetTexture | null = null;
  return {
    overlay, quad, target: () => rtt,
    setLayout: (layout, view) => {
      const { width, height } = layout.fit;
      if (rtt?.getSize().width !== width || rtt.getSize().height !== height) {
        rtt?.dispose();
        rtt = target(scene, width, height);
        puppet.outputRenderTarget = rtt;
        material.setTexture('puppet', rtt);
      }
      const f = overlayFrame(layout, view.originX, view.originY, view.pixelsPerUnit);
      Object.assign(overlay, { orthoLeft: f.ortho.left, orthoRight: f.ortho.right, orthoTop: f.ortho.top, orthoBottom: f.ortho.bottom });
      quad.position.set(f.quad.x, f.quad.y, DEPTH_RANGE.distance);
      quad.scaling.set(f.quad.width, f.quad.height, 1);
    },
  };
}
